import {
  getInboundJourneys,
  getNonTripOffers,
  getOutboundJourneys,
  resetPurchase,
} from '@/features/purchase/purchaseActions';
import { useDispatch } from '@/store/utils';
import { formatFullDate } from '@/utils/dateTime';
import { GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import type { SearchFormValues } from '@packages/webshop-search-widget';
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface SearchWidgetContextType {
  isSearchOverlayOpened: boolean;
  setIsSearchOverlayOpened: (isOpened: boolean) => void;
  currentSearchFormValues: SearchFormValues | undefined;
  setCurrentSearchFormValues: Dispatch<
    SetStateAction<SearchFormValues | undefined>
  >;
  handleSubmit: (values: SearchFormValues) => void;
}

export const SearchWidgetContext = createContext<
  SearchWidgetContextType | undefined
>(undefined);

export const SearchWidgetProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isSearchOverlayOpened, setIsSearchOverlayOpened] = useState(false);
  const [currentSearchFormValues, setCurrentSearchFormValues] = useState<
    SearchFormValues | undefined
  >();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    async (values: SearchFormValues) => {
      setCurrentSearchFormValues(values);
      trackCustomEvent(GTM_EVENT.SEARCH_INITIATED);

      const isTicketProductType = values.productType === 'ticket';

      dispatch(resetPurchase());
      navigate('/purchase');
      setIsSearchOverlayOpened(false);

      if (!values.origin?.value || !values.destination?.value) {
        return;
      }

      if (isTicketProductType) {
        const journeysResponses = await Promise.all([
          dispatch(
            getOutboundJourneys({
              originStop: { code: values.origin.value },
              destinationStop: { code: values.destination.value },
              departureTime: formatFullDate(values.departureDate),
              passengers: values.passengers,
              promoCodes: values.promoCodes,
              ...(values.returnDate
                ? {
                    returnSearchParameters: {
                      inwardReturnDate: formatFullDate(values.returnDate),
                    },
                  }
                : {}),
            })
          ).unwrap(),
          ...(values.returnDate
            ? [
                dispatch(
                  getInboundJourneys({
                    originStop: { code: values.destination.value },
                    destinationStop: { code: values.origin.value },
                    departureTime: formatFullDate(values.returnDate),
                    passengers: values.passengers,
                    promoCodes: values.promoCodes,
                    returnSearchParameters: {
                      outwardTripDate: formatFullDate(values.departureDate),
                    },
                  })
                ).unwrap(),
              ]
            : []),
        ]);

        if (
          journeysResponses.every(
            (response) => (response?.journeys?.length ?? 0) > 0
          )
        ) {
          navigate('/purchase/outbound/journey');
        }
      } else {
        const offersResponse = await dispatch(
          getNonTripOffers({
            originStop: { code: values.origin.value },
            destinationStop: { code: values.destination.value },
            validityStartTime: formatFullDate(values.departureDate),
            passengers: values.passengers,
            promoCodes: values.promoCodes,
          })
        ).unwrap();

        if ((offersResponse?.nonTripOffers?.length ?? 0) > 0) {
          navigate('/purchase/non-trip/offer');
        }
      }
    },
    [dispatch, navigate]
  );

  return (
    <SearchWidgetContext.Provider
      value={{
        isSearchOverlayOpened,
        setIsSearchOverlayOpened,
        currentSearchFormValues,
        setCurrentSearchFormValues,
        handleSubmit,
      }}
    >
      {children}
    </SearchWidgetContext.Provider>
  );
};
